import React from 'react'
import { Box, Flex, PrimaryButton, Text } from '@moonpig/launchpad-components'
import { system as s } from '@moonpig/launchpad-system'
import { styled } from '@moonpig/launchpad-utils'
import { IconSystemFilter } from '@moonpig/launchpad-assets'
import {
  FILTERS_MENU_BUTTON_HEIGHT_PX,
  FILTER_INDICATOR_SIZE_PX,
} from '../../../FiltersCarousel/constants'
import { useFindLocaleText } from '../../../../text-localisation'

const StyledToggleWrapper = styled(Flex)`
  position: relative;

  ${s({
    p: 0,
    borderRight: 0,
    borderColor: 'colorBlack10',
    display: 'inline-flex',
  })}
`

const StyledToggle = styled(PrimaryButton)<{
  filtersApplied: boolean
}>`
  transition: background-color 100ms ease-in-out, border-color 100ms ease-in-out,
    color 100ms ease-in-out;
  box-sizing: border-box;

  ${({ filtersApplied }) =>
    s({
      bgcolor: 'colorBackground01',
      border: 2,
      px: { xs: 5, md: 6 },
      py: 5,
      mr: 3,
      borderColor: filtersApplied
        ? 'colorInteractionSelectedState'
        : 'colorBlack20',
      color: filtersApplied
        ? 'colorInteractionSelectedState'
        : 'colorTextLabel',
      boxShadow: 0,
      borderRadius: 2,
      minHeight: FILTERS_MENU_BUTTON_HEIGHT_PX,
      width: { xs: '48px', md: 'auto' },
      height: '48px',
    })}

  &:not(:disabled) {
    &:hover {
      text-decoration: none;
      outline: none;

      ${({ filtersApplied }) =>
        s({
          boxShadow: 0,
          borderColor: filtersApplied
            ? 'colorInteractionSelectedState'
            : 'colorBlack30',
          bgcolor: filtersApplied
            ? 'colorBackgroundSelectedState'
            : 'colorBlack05',
        })}
    }

    &:focus {
      text-decoration: none;
      outline: none;
      ${({ filtersApplied }) =>
        s({
          boxShadow: 5,
          borderColor: filtersApplied
            ? 'colorInteractionSelectedState'
            : 'colorBlack30',
          bgcolor: filtersApplied
            ? 'colorBackgroundSelectedState'
            : 'colorBlack05',
        })}
    }
  }
`

const StyledFilterIndicator = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  width: ${FILTER_INDICATOR_SIZE_PX};
  height: ${FILTER_INDICATOR_SIZE_PX};
  transform: translate(50%, -50%);

  ${s({
    borderRadius: '50%',
    bgcolor: 'colorInteractionSelectedState',
    border: 2,
    borderColor: 'colorBackground01',
  })};
`

type ToggleProps = {
  onClick: () => void
  appliedFiltersCount: number
  isOpen: boolean
  ref?: React.Ref<HTMLAnchorElement>
}

export const Toggle = React.forwardRef<HTMLAnchorElement, ToggleProps>(
  ({ onClick, appliedFiltersCount, isOpen }, ref) => {
    const localiseText = useFindLocaleText()

    const filtersApplied = appliedFiltersCount > 0

    const leadingIcon = (
      <Box data-testid={'filters-menu-icon'}>
        <IconSystemFilter aria-hidden width="24px" />
      </Box>
    )

    return (
      <StyledToggleWrapper>
        <StyledToggle
          ref={ref}
          aria-label={localiseText('find.open_filters')}
          aria-expanded={isOpen}
          aria-controls="filters-menu"
          data-testid="filters-menu-toggle"
          onClick={onClick}
          leadingIcon={leadingIcon}
          filtersApplied={filtersApplied}
        >
          <Text
            display={{ xs: 'none', md: 'block' }}
            typography="typeButtonLabel"
          >
            {localiseText('find.filters')}
          </Text>
          {filtersApplied && (
            <StyledFilterIndicator
              aria-label={localiseText('find.n_filters_applied', {
                filterCount: appliedFiltersCount,
              })}
            />
          )}
        </StyledToggle>
      </StyledToggleWrapper>
    )
  },
)
